import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import {
  useConsumerAppointments,
  useConsumerStats,
  useUnreadCount,
  type ConsumerAppointment,
} from '@theraply/api-client';

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function getSessionTypeLabel(type: string): string {
  switch (type) {
    case 'INTAKE': return 'Initial Consultation';
    case 'REGULAR': return 'Regular Session';
    case 'CRISIS': return 'Crisis Session';
    default: return type;
  }
}

export default function DashboardPage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { data: appointmentsData, isLoading: appointmentsLoading, error: appointmentsError } = useConsumerAppointments();
  const { data: stats, isLoading: statsLoading } = useConsumerStats();
  const { data: unreadCount } = useUnreadCount();

  const upcomingAppointments: ConsumerAppointment[] = (appointmentsData?.bookings || [])
    .filter((b) => b.status === 'CONFIRMED')
    .sort((a, b) => {
      const dateA = `${a.slotDate}T${a.slotTime}`;
      const dateB = `${b.slotDate}T${b.slotTime}`;
      return dateA.localeCompare(dateB);
    })
    .slice(0, 3);

  const isLoading = appointmentsLoading || statsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (appointmentsError) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Dashboard</h3>
          <p className="text-red-600 text-sm">
            Failed to load your dashboard data. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">
        Welcome back, {user?.firstName || 'there'}!
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        Here's an overview of your mental wellness journey.
      </p>

      {/* Stats Cards */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-md bg-indigo-500 flex items-center justify-center">
                  <CalendarDaysIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Upcoming Sessions
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {upcomingAppointments.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-md bg-green-500 flex items-center justify-center">
                  <ClockIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Sessions
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {stats?.totalSessions ?? 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-md bg-blue-500 flex items-center justify-center relative">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                  {(unreadCount ?? 0) > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount! > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Unread Messages
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {unreadCount ?? 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Appointments */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Appointments</h2>
          <button
            onClick={() => navigate('/appointments')}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
          >
            View all
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>

        {upcomingAppointments.length > 0 ? (
          <div className="space-y-4">
            {upcomingAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="bg-white shadow rounded-lg p-5 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <UserCircleIcon className="h-8 w-8 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {appointment.therapistFirstName} {appointment.therapistLastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(appointment.slotDate)} at {formatTime(appointment.slotTime)}
                    </p>
                    <span className="inline-block mt-1 text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                      {getSessionTypeLabel(appointment.sessionType)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/session/${appointment.id}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  <VideoCameraIcon className="h-4 w-4" />
                  Join
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No upcoming appointments</p>
            <p className="text-gray-500 text-sm mt-1">
              Find a therapist and book your first session.
            </p>
            <button
              onClick={() => navigate('/therapists')}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              Find a Therapist
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/therapists')}
            className="bg-white shadow rounded-lg p-5 text-left hover:shadow-md transition-shadow group"
          >
            <MagnifyingGlassIcon className="h-8 w-8 text-indigo-500 group-hover:text-indigo-600 mb-3" />
            <p className="font-medium text-gray-900">Find a Therapist</p>
            <p className="text-sm text-gray-500 mt-1">Browse and connect with therapists</p>
          </button>

          <button
            onClick={() => navigate('/appointments')}
            className="bg-white shadow rounded-lg p-5 text-left hover:shadow-md transition-shadow group"
          >
            <CalendarDaysIcon className="h-8 w-8 text-green-500 group-hover:text-green-600 mb-3" />
            <p className="font-medium text-gray-900">Appointments</p>
            <p className="text-sm text-gray-500 mt-1">Manage your scheduled sessions</p>
          </button>

          <button
            onClick={() => navigate('/messages')}
            className="bg-white shadow rounded-lg p-5 text-left hover:shadow-md transition-shadow group relative"
          >
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-500 group-hover:text-blue-600 mb-3" />
            <p className="font-medium text-gray-900">Messages</p>
            <p className="text-sm text-gray-500 mt-1">Chat with your therapist</p>
            {(unreadCount ?? 0) > 0 && (
              <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount! > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={() => navigate('/profile')}
            className="bg-white shadow rounded-lg p-5 text-left hover:shadow-md transition-shadow group"
          >
            <UserCircleIcon className="h-8 w-8 text-purple-500 group-hover:text-purple-600 mb-3" />
            <p className="font-medium text-gray-900">Profile</p>
            <p className="text-sm text-gray-500 mt-1">Update your personal information</p>
          </button>
        </div>
      </div>
    </div>
  );
}
